// The chain's canvas as a PNG, without an image library: the canvas's
// viewport is cloned, set at the bounds of its cards, wrapped with the
// page's style rules in an SVG foreign object, drawn on a canvas and saved.
// Fonts the page loads from another origin are not carried; the image falls
// back to the system faces.

import { Node, getNodesBounds } from "@xyflow/react";

// Space (px) around the cards in the image
const PADDING = 32;

// Room above the cards for the line of the column heads
const HEAD_ROOM = 40;

// Pixels per CSS pixel of the image
const SCALE = 2;

// The style rules of the page that can be read: the ones of another origin
// cannot, and are left out
const pageCss = (): string =>
  Array.from(document.styleSheets)
    .flatMap((sheet) => {
      try {
        return Array.from(sheet.cssRules).map((rule) => rule.cssText);
      } catch {
        return [];
      }
    })
    .join("\n");

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("The chain could not be drawn."));
    image.src = src;
  });

// The chain drawn by the canvas as a PNG data address. `flow` is the
// canvas's own element; its viewport holds the cards, the edges and the
// heads. `background` fills the image.
export const chainPng = async (
  flow: HTMLElement,
  nodes: Node[],
  background: string,
): Promise<string> => {
  const viewport = flow.querySelector<HTMLElement>(".react-flow__viewport");
  if (!viewport || nodes.length === 0) {
    throw new Error("There is no chain to export.");
  }
  const bounds = getNodesBounds(nodes);
  const width = Math.ceil(bounds.width + PADDING * 2);
  const height = Math.ceil(bounds.height + PADDING * 2 + HEAD_ROOM);

  const clone = viewport.cloneNode(true) as HTMLElement;
  clone.style.transform = `translate(${PADDING - bounds.x}px, ${
    PADDING + HEAD_ROOM - bounds.y
  }px) scale(1)`;
  // The canvas's own classes carry the variables its styles read
  const frame = document.createElement("div");
  frame.className = flow.className;
  frame.setAttribute(
    "style",
    `position:relative;width:${width}px;height:${height}px;overflow:hidden;background:${background}`,
  );
  frame.appendChild(clone);
  const style = document.createElement("style");
  style.textContent = pageCss();
  const body = document.createElement("div");
  body.setAttribute("xmlns", "http://www.w3.org/1999/xhtml");
  body.appendChild(style);
  body.appendChild(frame);

  const svg = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">`,
    `<foreignObject x="0" y="0" width="100%" height="100%">`,
    new XMLSerializer().serializeToString(body),
    "</foreignObject></svg>",
  ].join("");
  const image = await loadImage(
    `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`,
  );

  const canvas = document.createElement("canvas");
  canvas.width = width * SCALE;
  canvas.height = height * SCALE;
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("The chain could not be drawn.");
  }
  context.scale(SCALE, SCALE);
  context.fillStyle = background;
  context.fillRect(0, 0, width, height);
  context.drawImage(image, 0, 0);
  return canvas.toDataURL("image/png");
};

// Hands a PNG data address to the browser as a file to save
export const downloadPng = (filename: string, dataUrl: string): void => {
  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
};

// The file name of an export: the root's id
export const chainPngFilename = (rootId: string): string =>
  `value-chain-${rootId.replace(/[^A-Za-z0-9_-]+/g, "-")}.png`;
